import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';
import { fs as backdropFS, vs as backdropVS } from './backdrop-shader';
import { vs as sphereVS } from './sphere-shader';
import { Analyser } from './analyser';

const AudioOrbVisual = ({ inputNode, outputNode }) => {
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  const composerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const inputAnalyserRef = useRef(null);
  const outputAnalyserRef = useRef(null);
  const sphereRef = useRef(null);
  const backdropRef = useRef(null);
  const cameraRef = useRef(null);
  const rotationRef = useRef(new THREE.Vector3(0, 0, 0));
  const prevTimeRef = useRef(0);

  useEffect(() => {
    if (inputNode) {
      inputAnalyserRef.current = new Analyser(inputNode);
    }
  }, [inputNode]);

  useEffect(() => {
    if (outputNode) {
      outputAnalyserRef.current = new Analyser(outputNode);
    }
  }, [outputNode]);

  useEffect(() => {
    if (!canvasRef.current) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x100c14);

    const backdrop = new THREE.Mesh(
      new THREE.IcosahedronGeometry(10, 5),
      new THREE.RawShaderMaterial({
        uniforms: {
          resolution: { value: new THREE.Vector2(1, 1) },
          rand: { value: 0 },
        },
        vertexShader: backdropVS,
        fragmentShader: backdropFS,
        glslVersion: THREE.GLSL3,
      }),
    );
    backdrop.material.side = THREE.BackSide;
    scene.add(backdrop);
    backdropRef.current = backdrop;

    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000,
    );
    camera.position.set(2, -2, 5);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: false,
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio / 1);
    rendererRef.current = renderer;

    const geometry = new THREE.IcosahedronGeometry(1, 10);
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();

    const sphereMaterial = new THREE.MeshStandardMaterial({
      color: 0x000010,
      metalness: 0.5,
      roughness: 0.1,
      emissive: 0x000010,
      emissiveIntensity: 1.5,
    });

    sphereMaterial.onBeforeCompile = (shader) => {
      shader.uniforms.time = { value: 0 };
      shader.uniforms.inputData = { value: new THREE.Vector4() };
      shader.uniforms.outputData = { value: new THREE.Vector4() };

      sphereMaterial.userData.shader = shader;

      shader.vertexShader = sphereVS;
    };

    const sphere = new THREE.Mesh(geometry, sphereMaterial);
    sphere.position.y = 0.5;
    scene.add(sphere);
    sphere.visible = false;
    sphereRef.current = sphere;

    new EXRLoader().load('/piz_compressed.exr', (texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      const exrCubeRenderTarget = pmremGenerator.fromEquirectangular(texture);
      sphereMaterial.envMap = exrCubeRenderTarget.texture;
      sphere.visible = true;
    });

    const renderPass = new RenderPass(scene, camera);

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      5,
      0.5,
      0,
    );

    const fxaaPass = new ShaderPass(FXAAShader);

    const composer = new EffectComposer(renderer);
    composer.addPass(renderPass);
    // composer.addPass(fxaaPass);
    composer.addPass(bloomPass);
    composerRef.current = composer;

    const onWindowResize = () => {
      if (!cameraRef.current || !rendererRef.current || !composerRef.current) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const dPR = rendererRef.current.getPixelRatio();
      
      cameraRef.current.aspect = w / h;
      cameraRef.current.updateProjectionMatrix();
      
      backdrop.material.uniforms.resolution.value.set(w * dPR, h * dPR);
      rendererRef.current.setSize(w, h);
      composerRef.current.setSize(w, h);
      
      if (fxaaPass.material.uniforms['resolution']) {
          fxaaPass.material.uniforms['resolution'].value.set(
            1 / (w * dPR),
            1 / (h * dPR),
          );
      }
    };

    window.addEventListener('resize', onWindowResize);
    onWindowResize();

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      if (inputAnalyserRef.current) inputAnalyserRef.current.update();
      if (outputAnalyserRef.current) outputAnalyserRef.current.update();

      const t = performance.now();
      const dt = (t - prevTimeRef.current) / (1000 / 60);
      prevTimeRef.current = t;

      if (backdropRef.current) {
          backdropRef.current.material.uniforms.rand.value = Math.random() * 10000;
      }

      if (sphereRef.current && sphereRef.current.material.userData.shader) {
        const inputData = inputAnalyserRef.current ? inputAnalyserRef.current.data : new Uint8Array(32);
        const outputData = outputAnalyserRef.current ? outputAnalyserRef.current.data : new Uint8Array(32);

        sphereRef.current.scale.setScalar(
          1 + (0.2 * outputData[1]) / 255,
        );

        const f = 0.001;
        rotationRef.current.x += (dt * f * 0.5 * outputData[1]) / 255;
        rotationRef.current.z += (dt * f * 0.5 * inputData[1]) / 255;
        rotationRef.current.y += (dt * f * 0.25 * inputData[2]) / 255;
        rotationRef.current.y += (dt * f * 0.25 * outputData[2]) / 255;

        const euler = new THREE.Euler(
          rotationRef.current.x,
          rotationRef.current.y,
          rotationRef.current.z,
        );
        const quaternion = new THREE.Quaternion().setFromEuler(euler);
        const vector = new THREE.Vector3(0, 0, 5);
        vector.applyQuaternion(quaternion);
        
        if (cameraRef.current) {
            cameraRef.current.position.copy(vector);
            cameraRef.current.lookAt(sphereRef.current.position);
        }

        const shader = sphereRef.current.material.userData.shader;
        shader.uniforms.time.value += (dt * 0.1 * outputData[0]) / 255;
        shader.uniforms.inputData.value.set(
          (1 * inputData[0]) / 255,
          (0.1 * inputData[1]) / 255,
          (10 * inputData[2]) / 255,
          0,
        );
        shader.uniforms.outputData.value.set(
          (2 * outputData[0]) / 255,
          (0.1 * outputData[1]) / 255,
          (10 * outputData[2]) / 255,
          0,
        );
      }

      if (composerRef.current) {
          composerRef.current.render();
      }
    };

    animate();

    return () => {
      window.removeEventListener('resize', onWindowResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Cleanup Three.js resources if needed
      if (rendererRef.current) {
          rendererRef.current.dispose();
      }
    };
  }, []); // Run once on mount

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }} />;
};

export default AudioOrbVisual;
